import type { LevelConfig, LevelPiece } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as
// tilted_table_showcase.ts's local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

const COLS = 9;
const LAYERS = 2;

/** Middle columns left empty on the shelf below, for the upper table's leg. */
const LEG_GAP_COLS = [ 3, 4, 5 ];

/** How tall the base deck's stack is — the case this level exists to prove. */
const BASE_ROWS = 4;

/**
 * Static test level for TableConfig.standOn (see level_config.ts,
 * PhysicsTable#standOn and GameScreen#shelfClearance): tables standing ON one
 * another, feet on the table below's top, so each lower deck reads as a shelf
 * for the one above.
 *
 * What to look for:
 *   - the shelf's LEG is no longer authored-length: GameScreen measures the
 *     base table's own layers (BASE_ROWS here) and stretches the leg tall
 *     enough to clear that stack, so raising BASE_ROWS alone is enough — no
 *     manual height to keep in sync;
 *   - each deck is otherwise an ORDINARY table — full stack of blocks, its own
 *     legs — it just starts higher up, and the middle deck carries a shelf of
 *     its own the same way the bottom one does;
 *   - the shelf's blocks still have to clear whatever stands ON it and keep
 *     out of where its own leg lands; GameScreen enforces both and warns about
 *     anything it had to drop.
 */
function makeDeck( rows: number, gap_cols: number[] ): { grid: number[][]; pieces: LevelPiece[] }
{
  const grid: number[][] = [];
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let row = 0; row < rows; row++ )
  {
    const grid_row: number[] = [];

    for ( let col = 0; col < COLS; col++ )
    {
      if ( gap_cols.includes( col ) )
      {
        grid_row.push( 0 );
        continue;
      }

      grid_row.push( id );
      pieces.push( { id, type: 2, x: col, y: row, width: 1, height: 1 } );
      id++;
    }

    grid.push( grid_row );
  }

  return { grid, pieces };
}

function makeLayers( rows: number, gap_cols: number[] )
{
  return Array.from( { length: LAYERS }, () => makeDeck( rows, gap_cols ) );
}

const SHELF_TABLE_LEVEL: LevelConfig = {
  level: 90305,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    // Lower deck: on the ground, blocks parted around the shelf's leg.
    {
      layers: makeLayers( 2, LEG_GAP_COLS ),
      slot: { u: 0, v: 0 },
    },
    // Middle deck: stands on the base, and carries a shelf of its own — so it
    // needs the same leg gap the base does.
    {
      layers: makeLayers( 2, LEG_GAP_COLS ),
      slot: { u: 0, v: 0 },
      standOn: 0,
    },
    // Top deck: an ordinary table that happens to stand on the middle one.
    // Nothing stands on this one, so its layers need no gap.
    {
      layers: makeLayers( 3, [] ),
      slot: { u: 0, v: 0 },
      standOn: 1,
    },
  ],
};

export class ShelfTableShowcase
{
  public static readonly LEVEL = SHELF_TABLE_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? SHELF_TABLE_LEVEL : null;
  }
}
